/** Maximum match % shown to users (never 100% — credibility). */
export const MAX_MATCH_PERCENT = 97;

/** Normalize any match score to a user-facing percentage (0–97). */
export function capMatchPercent(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 0;
  let percent = n;
  if (percent > 0 && percent <= 1) percent *= 100;
  return Math.round(Math.max(0, Math.min(MAX_MATCH_PERCENT, percent)));
}

/** Normalize API matchScore (0–100 or 0–1) to a display percentage. */
export function getMatchPercent(product) {
  const raw = product?.matchScore ?? product?.score ?? product?.match;
  return capMatchPercent(raw);
}

/** Pick the home-page top match from compared products (rank first, then match score). */
export function pickComparisonWinnerId(products = []) {
  const scored = products
    .map((product) => ({
      id: String(product._id || product.id || product.productId || ""),
      rank:
        product.rank != null && Number.isFinite(Number(product.rank))
          ? Number(product.rank)
          : null,
      matchScore: product.matchScore ?? product.score ?? product.match ?? null,
    }))
    .filter((entry) => entry.id);

  if (!scored.length) return null;

  const hasRank = scored.some((entry) => entry.rank != null);
  if (hasRank) {
    return [...scored].sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99))[0].id;
  }

  const hasMatch = scored.some(
    (entry) =>
      entry.matchScore != null && Number.isFinite(Number(entry.matchScore)),
  );
  if (hasMatch) {
    return [...scored].sort(
      (a, b) => Number(b.matchScore) - Number(a.matchScore),
    )[0].id;
  }

  return null;
}

/** Match badge color tier for UI (green → amber → orange). */
export function getMatchBadgeTier(score) {
  const percent = capMatchPercent(score);
  if (percent >= 80) return "excellent";
  if (percent >= 60) return "good";
  if (percent >= 40) return "fair";
  return "low";
}

// --- Fit score display semantics -----------------------------------------
// "Fit score" replaces "% match" copy sitewide: the underlying number is
// unchanged (still 0–97, still backed by `matchScore` in the API/storage
// for compatibility), but the wording avoids implying a probability of
// satisfaction/purchase — it's an ordinal fit rating, not a percent chance.
// Centralizing the copy here (and in matchLabelContext.js, which calls
// into these helpers) means every consuming surface picks up wording
// changes for free instead of re-implementing "N% match" strings inline.

/** Ordinal fit band label — no probability language. */
export function getFitScoreBandLabel(score) {
  const tier = getMatchBadgeTier(score);
  switch (tier) {
    case "excellent":
      return "Excellent fit";
    case "good":
      return "Good fit";
    case "fair":
      return "Fair fit";
    default:
      return "Limited fit";
  }
}

/** Full "Fit score 82/100" label used in tooltips/aria/detailed copy. */
export function formatFitScoreLabel(score) {
  if (score == null || !Number.isFinite(Number(score))) return null;
  return `Fit score ${capMatchPercent(score)}/100`;
}

/** Compact "82 Fit score" label for tight inline badges (e.g. product cards). */
export function formatFitScoreBadge(score) {
  if (score == null || !Number.isFinite(Number(score))) return null;
  return `${capMatchPercent(score)} Fit score`;
}

// Legacy stored/cached copy and analytics-adjacent strings may still carry
// the old "N% match" prefix for a while (cached session blurbs, historical
// analytics event payloads, etc.). Recognize both shapes so cleanup/parsing
// code doesn't break for users whose local/cached copy hasn't rotated yet.
const LEGACY_MATCH_PREFIX_RE = /^\s*\d+%\s*match\b/i;
const FIT_SCORE_PREFIX_RE = /^\s*(fit score\s*\d+\s*\/\s*100|\d+\s*fit score)\b/i;

/** True if `label` starts with either the legacy or current match/fit prefix. */
export function hasMatchOrFitScorePrefix(label) {
  if (!label) return false;
  const str = String(label);
  return LEGACY_MATCH_PREFIX_RE.test(str) || FIT_SCORE_PREFIX_RE.test(str);
}

/** Map broad matchProfile → match intent answers for primitive scoring. */
export function matchProfileToWizardAnswers(profile) {
  if (!profile || typeof profile !== "object") return {};

  const categoryName = profile.category?.name || profile.category || null;
  const budget = profile.budget || {};
  const hasBudget =
    (budget.min != null && budget.min !== "") ||
    (budget.max != null && budget.max !== "");

  const mustHaves = (profile.mustHaves || []).map((m) =>
    typeof m === "string" ? m : m?.label,
  ).filter(Boolean);

  const wizardAnswers = {
    type: categoryName || undefined,
    budget: hasBudget
      ? { min: budget.min ?? undefined, max: budget.max ?? undefined }
      : undefined,
    mustHaves,
    niceToHaves: profile.niceToHaves || [],
    useCase: profile.primaryUseCase || profile.useCases?.[0] || undefined,
    featurePriorities: profile.featurePriorities || {},
    dealBreakers: profile.dealBreakers || [],
    notesForEngine: profile.notesForEngine || "",
  };

  const preferredBrand = profile.brands?.preferred?.[0];
  if (preferredBrand) wizardAnswers.brand = preferredBrand;

  return wizardAnswers;
}

/** Persist chat match profile for primitive scoring sitewide. */
export function persistMatchProfile(matchProfile) {
  if (!matchProfile || typeof matchProfile !== "object") return;
  const wizardAnswers = matchProfileToWizardAnswers(matchProfile);
  localStorage.setItem("wizardAnswers", JSON.stringify(wizardAnswers));
  localStorage.setItem("matchProfile", JSON.stringify(matchProfile));
  localStorage.setItem("wizardResultsActive", "true");
}

/** Persist AI chat intent for primitive scoring (searchParams shape). */
export function persistWizardAnswersFromChat(searchParams) {
  if (!searchParams || typeof searchParams !== "object") return;

  const budget = searchParams.budget;
  const hasBudget =
    budget &&
    (budget.min != null && budget.min !== "" || budget.max != null && budget.max !== "");

  const wizardAnswers = {
    type: searchParams.category || undefined,
    budget: hasBudget ? budget : undefined,
    mustHaves: searchParams.mustHaves || [],
    niceToHaves: searchParams.niceToHaves || [],
    useCase: searchParams.useCase || undefined,
  };

  localStorage.setItem("wizardAnswers", JSON.stringify(wizardAnswers));
  localStorage.setItem("wizardResultsActive", "true");
}
